import {
  Form,
  Modal,
  Input,
  Button,
  Space,
  Transfer,
  message,
  Skeleton,
} from 'antd';

import './manageTemplateModal.scss';
import { useCallback, useEffect, useState } from 'react';
import BasicHTMLEditor, { HTMLEditorContent } from '../../BasicHTMLEditor';
import {
  GetAllClientsQueryResult,
  useAssignTemplateToClientsMutation,
  useCreateTemplateMutation,
  useDisassociateTemplateFromClientsMutation,
  useGetAllClientsQuery,
  useGetTemplateClientsQuery,
  useUpdateTemplateMutation,
} from '../../../graphql/datamodel.gen';
import { TransferDirection, TransferListProps } from 'antd/lib/transfer';
import { ReloadOutlined } from '@ant-design/icons';
import { useForm } from 'antd/lib/form/Form';

type Props = {
  show: boolean;
  onClose: () => void;
  template?: {
    id: number;
    name: string;
    heading: HTMLEditorContent;
    body: HTMLEditorContent;
  };
};

const computeTransferMovements = (
  currentState: string[],
  targetKeys: string[],
) => {
  const added = targetKeys.filter((t) => !currentState.includes(t));
  const removed = currentState.filter((t) => !targetKeys.includes(t));

  return {
    added,
    removed,
  };
};

const ManageTemplateModal = ({
  show,
  onClose: onCloseProp,
  template,
}: Props) => {
  const mode: 'create' | 'edit' = template ? 'edit' : 'create';

  const [form] = Form.useForm();

  const [createTemplateMut, { loading: createTemplateLoading }] =
    useCreateTemplateMutation();
  const [updateTemplateMut, { loading: updateTemplateLoading }] =
    useUpdateTemplateMutation();
  const [disassociateTemplateFromClientsMut] =
    useDisassociateTemplateFromClientsMutation();
  const [assignTemplateToClientsMut] = useAssignTemplateToClientsMutation();

  const { data: getTemplateClients, refetch: refetchTemplateClients } =
    useGetTemplateClientsQuery({
      fetchPolicy: 'cache-and-network',
      skip: !template?.id,
      variables: {
        id: template?.id,
      },
    });

  const {
    data: getAllClientsData,
    refetch: refetchAllClients,
    loading: loadingAllClients,
  } = useGetAllClientsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const templateClients = getTemplateClients?.template?.clients || [];
  const allClients = getAllClientsData?.clients || [];

  const onClose = useCallback(() => {
    onCloseProp();
  }, [form, onCloseProp]);

  useEffect(() => {
    if (show) {
      refetchTemplateClients().then(() => {
        refetchAllClients();
      });
    }
  }, [show]);

  const onSubmitForm = useCallback(
    async ({
      name,
      heading,
      body,
      clients,
    }: {
      name: string;
      heading: Record<string, unknown>;
      body: Record<string, unknown>;
      clients: string[];
    }) => {
      let templateId;

      if (mode === 'create') {
        const createdTemplate = await createTemplateMut({
          variables: {
            name,
            heading: heading,
            body: body,
          },
        });

        templateId = createdTemplate.data?.createTemplate?.id;
      }

      if (mode === 'edit') {
        const updatedTemplate = await updateTemplateMut({
          variables: {
            id: template.id,
            body,
            heading,
            name,
          },
        });

        templateId = updatedTemplate.data?.updateTemplate?.id;
      }

      const { added, removed } = computeTransferMovements(
        templateClients.map((c) => c.volcanoClientId),
        clients,
      );

      if (added.length > 0) {
        await assignTemplateToClientsMut({
          variables: {
            templateId,
            clients: added,
          },
        });
      }

      if (removed.length > 0) {
        await disassociateTemplateFromClientsMut({
          variables: {
            templateId,
            clients: removed,
          },
        });
      }

      onClose();
    },
    [
      mode,
      templateClients,
      onClose,
      createTemplateMut,
      updateTemplateMut,
      template?.id,
      assignTemplateToClientsMut,
      disassociateTemplateFromClientsMut,
    ],
  );

  const validateHTMLTemplate = useCallback((rule, value) => {
    if (!value) {
      return Promise.reject('Por favor ingrese un valor');
    }

    return Promise.resolve();
  }, []);

  const renderFooter = useCallback(
    (
      _: TransferListProps<any>,
      {
        direction,
      }: {
        direction: TransferDirection;
      },
    ) => {
      if (direction === 'left') {
        return (
          <Space direction="horizontal" style={{ margin: 5 }}>
            <Button
              size="small"
              onClick={() => {
                refetchAllClients();
              }}
              loading={loadingAllClients}
              icon={<ReloadOutlined translate={undefined} />}
            >
              Refrescar
            </Button>
          </Space>
        );
      }
    },
    [loadingAllClients, refetchAllClients],
  );

  const loadingClients = !getAllClientsData && loadingAllClients;

  return (
    <Modal
      centered
      title={
        mode === 'create'
          ? 'Crear plantilla'
          : `Editar plantilla "${template?.name || ''}"`
      }
      destroyOnClose
      width={'auto'}
      visible={show}
      bodyStyle={{
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
      onCancel={onClose}
      closable
      className="manage-template-modal"
      okText="Hecho"
      okButtonProps={{
        htmlType: 'submit',
        form: 'manage-template-form',
        loading: createTemplateLoading || updateTemplateLoading,
      }}
    >
      <Form
        form={form}
        layout="vertical"
        id="manage-template-form"
        name="manage-template-form"
        onFinish={onSubmitForm}
        initialValues={{
          name: template?.name,
          heading: template?.heading,
          body: template?.body,
          clients: templateClients?.map((c) => c.volcanoClientId),
        }}
      >
        <Form.Item
          required
          requiredMark
          label="Nombre"
          name={'name'}
          rules={[
            {
              required: true,
              message: 'Por favor ingrese un nombre para la plantilla',
            },
          ]}
          tooltip="Dale un nombre a la plantilla para que la puedas identificar más adelante"
        >
          <Input placeholder="Ej: plantilla presupuesto estándar" />
        </Form.Item>

        <Form.Item
          rules={[
            {
              validator: validateHTMLTemplate,
            },
          ]}
          name={'heading'}
          required
          requiredMark
          label="Cabecera"
        >
          <BasicHTMLEditor required />
        </Form.Item>
        <Form.Item
          rules={[
            {
              validator: validateHTMLTemplate,
            },
          ]}
          name={'body'}
          required
          requiredMark
          label="Cuerpo"
        >
          <BasicHTMLEditor required />
        </Form.Item>
        <Form.Item name="clients" label="Clientes" valuePropName="targetKeys">
          <Transfer
            titles={[
              'Clientes sin asignar',
              'Clientes asignados a esta plantilla',
            ]}
            dataSource={[...allClients]}
            render={(i) => i.name}
            listStyle={{
              width: 400,
            }}
            pagination={{
              pageSize: 10,
            }}
            disabled={loadingClients}
            operations={['Asignar', 'Desasignar']}
            showSearch
            showSelectAll
            rowKey={(c) => c.volcanoClientId}
            // @ts-ignore
            footer={renderFooter}
            filterOption={(inputValue, item) => {
              return (
                item.name?.toLowerCase()?.includes(inputValue?.toLowerCase()) ||
                inputValue?.toLowerCase()?.includes(item.name?.toLowerCase())
              );
            }}
            // onChange={(targetKeys, direction, moveKeys) => {
            //   const keys = moveKeys;

            //   const clientsToMove = keys.reduce((prev, curr) => {
            //     const matchingClient = allClients.find((c) => {
            //       return c.id === curr || c.volcanoClientId === curr;
            //     });

            //     return [...prev, matchingClient];
            //   }, []) as GetAllClientsQueryResult['data']['clients'];

            //   if (direction === 'left') {
            //     disassociateTemplateFromClientsMut({
            //       variables: {
            //         clients: clientsToMove.map((c) => c.id),
            //         templateId: template?.id,
            //       },
            //       onCompleted: () => {
            //         // clientsQuery.refetch();
            //         message.success('Clientes desasignados correctamente');
            //       },
            //     });
            //   }

            //   if (direction === 'right') {
            //     //assign
            //     assignTemplateToClientsMut({
            //       variables: {
            //         clients: clientsToMove.map((c) => c.id),
            //         templateId: template.id,
            //       },
            //       onCompleted: () => {
            //         // clientsQuery.refetch();
            //         message.success('Clientes asignados correctamente');
            //       },
            //     });
            //   }
            // }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManageTemplateModal;
